import { GRAY_OUT_OPACITY, VIEW_ROLE } from "constants/forge";
import { flatten } from "lodash-es";
import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import {
  clearHighlightObject,
  getViewerRole,
  highlightObject,
} from "utils/forge";
import {} from "utils/forge/forge3d";

export type PinObject = {
  dbIds: number[];
  color: string;
};

const useBehaviorPinObjects = () => {
  const { isLoadedViewerModelData } = useSelector(
    (state: RootState) => state.forgeViewer
  );

  const dbIdsAddedRef = useRef<number[]>([]);
  const pinObjectRef = useRef<number[]>([]);
  const viewerRoleRef = useRef<VIEW_ROLE>();

  const clearHighlightPinObject = useCallback(
    (color?: string, isResetThemingColor = false) => {
      if (!viewerRoleRef.current) {
        return;
      }
      const opacity = color ? 1 : GRAY_OUT_OPACITY;
      const pinDbIds = pinObjectRef.current || [];
      const addedDbIds = dbIdsAddedRef.current;
      const viewerRole = getViewerRole();
      const lastViewerRole = viewerRoleRef.current;
      if (viewerRole !== lastViewerRole) {
        return;
      }
      clearHighlightObject(
        lastViewerRole,
        pinDbIds,
        color,
        opacity,
        undefined,
        isResetThemingColor
      );
      clearHighlightObject(
        lastViewerRole,
        addedDbIds,
        color,
        opacity,
        undefined,
        isResetThemingColor
      );
    },
    []
  );

  /**
   * will highlight pin objects and pin object added
   */
  const highlightPinObjects = useCallback(
    (params: {
      pinObjects?: PinObject[];
      pinObjectSelected?: PinObject;
      newPinObjectAdded?: PinObject;
      colorReset?: string;
    }) => {
      if (!isLoadedViewerModelData) {
        return;
      }
      const { pinObjects, pinObjectSelected, newPinObjectAdded, colorReset } =
        params;
      // clear highlight by render proxy
      clearHighlightPinObject(colorReset);
      if (!pinObjects?.length && !newPinObjectAdded) {
        return;
      }

      const dbIdsAdded = newPinObjectAdded?.dbIds || [];
      dbIdsAddedRef.current = dbIdsAdded;
      pinObjectRef.current = flatten(
        pinObjects?.map((pinObject) => pinObject.dbIds)
      );

      viewerRoleRef.current = getViewerRole();

      pinObjects?.map((pinObject) => {
        if (pinObject?.color) {
          // highlight pin objects, new pin added objects
          const dbIds = (pinObject?.dbIds || []).filter(
            (dbId) => !dbIdsAdded.includes(dbId)
          );
          highlightObject({
            dbIds,
            color: pinObject.color,
          });
        }
      });

      if (pinObjectSelected?.dbIds.length) {
        highlightObject({
          dbIds: pinObjectSelected.dbIds,
          color: pinObjectSelected.color,
        });
      }

      if (dbIdsAdded && newPinObjectAdded?.color) {
        highlightObject({
          dbIds: dbIdsAdded,
          color: newPinObjectAdded.color,
        });
      }
    },
    [clearHighlightPinObject, isLoadedViewerModelData]
  );

  return { highlightPinObjects, clearHighlightPinObject };
};

export default useBehaviorPinObjects;
